import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { ClassNameMap, withStyles } from '@mui/styles';
import 'moment/locale/es';
import 'moment/locale/fr';
import { useLazyFetchReceiptQuery } from '../../../services/receipt.api';
import { ROUTES } from '../../../types/routes.enum';
import { BUTTON_STATUS } from '../interfaces';
import ChargerSessionWarningComponent from './charger-session-warning.component';
import { CostBreakdownItem, IdleFeesStatus, Session } from '../dto/charger.dto';
import ChargerCostDetails from './charger-cost-details.components';
import SessionButton from './charger-session-button.component';
import ChargerSessionDetails from './charger-session-details.components';
import { SessionStyle } from './styles/styles';
import ChargerIdlingComponent from './charger-idling.component';
import { PricingType } from '../../../stores/types/price.interface';
import ChargerRestartedComponent from './charger-restarted.component';
import FeedbackWithOptions from '../../feedback/feedback-with-options.component';
import CustomButton from '../../react-native-material-wrapper/button.component';
import { ConnectorStatus } from '../../../types/connector-status.enum';
import { LoadingDots } from '../../ui-components/loading-dots/loading-dots.component';
import { useSelector } from 'react-redux';
import { getTheme } from '../../../stores/selectors/theme.selector';
import { dataTestIds } from '../../../constant/dataTestId';
import { useAuth } from '../../../hooks/useAuth';
import { SessionOutlineButton } from './charger-session-outline-button.component';
import { usePostHog } from 'posthog-js/react';
import PromoCode from './promo-code/promo-code.component';
import { getAppliedPromoFromSession } from './promo-code/promo-code.utils';

interface IProps {
  classes: ClassNameMap;
  buttonStatus: BUTTON_STATUS;
  activeSession: Session;
  transactionId: string;
  onSessionStopped: Function;
  charger: any;
  showTechView: boolean;
  transaction: any;
  isChargingBySelf?: boolean;
  stopCharge: Function;
  loading: boolean;
}

const ChargerSession = ({
  buttonStatus,
  classes,
  activeSession,
  transactionId,
  onSessionStopped,
  charger,
  showTechView = false,
  transaction,
  isChargingBySelf = false,
  stopCharge,
  loading,
}: IProps) => {
  const { i18n } = useTranslation();
  const theme = useSelector(getTheme);
  const { isLoggedIn } = useAuth();
  const posthog = usePostHog();
  const receiptId = transaction?.receiptId;

  // redirect to the receipt page after the session is done and user clicks on "View full receipt"
  const handleReceiptClick = () => {
    const url = `/${ROUTES.RECEIPTS}/${receiptId}
      ?transaction=${transactionId}${charger?.name ? `&chargerName=${charger?.name}` : ''}`;
    // Open the receipt page based on the url
    window.location.assign(url);
  };

  const [triggerFetchReceipt, { data: receipt }] = useLazyFetchReceiptQuery();

  useEffect(() => {
    if (!isLoggedIn) {
      posthog.capture('anonymous_session_page_loaded', {
        sessionId: activeSession?.sessionId,
        chargerName: charger?.name,
        isLoggedIn: isLoggedIn,
      });
    }
  }, []);

  useEffect(() => {
    if (receiptId) {
      // we got receipt id means the charger is stopped.
      triggerFetchReceipt(receiptId);
      onSessionStopped();
    }
  }, [transaction, receiptId, onSessionStopped, triggerFetchReceipt]);

  // Check receiptId to make sure we are using the final transaction information when the session is stopped
  const sessionDetails = useMemo(() => {
    if (!receiptId && !activeSession.runningTotals) return;

    if (!receiptId) {
      return {
        cost: activeSession.runningTotals.cost,
        consumedEnergyKilowattHours: activeSession.runningTotals.consumedEnergyKilowattHours,
        pluggedInTimeSeconds: activeSession.runningTotals.pluggedInTimeSeconds,
      };
    }

    return {
      cost: transaction?.billedTotalAmount,
      consumedEnergyKilowattHours: transaction?.consumedEnergyKilowattHours,
      pluggedInTimeSeconds: transaction?.pluggedInTimeSeconds,
    };
  }, [activeSession, transaction, receiptId]);

  // BF-2556 "costBreakdown" doesn't always have a value, sometimes it may return null.
  const costItemsLength: number = activeSession?.runningTotals?.costBreakdown?.length || 0;

  const lastCostItem: CostBreakdownItem | null =
    costItemsLength > 0 ? activeSession.runningTotals.costBreakdown[costItemsLength - 1] : null;

  const renderLoading = () => {
    return (
      <div className={classes.buttonWrapper}>
        <CustomButton
          variant='contained'
          className={classes.buttonStyles}
          style={{
            backgroundColor: theme?.brand_0,
          }}
          disabled
        >
          <LoadingDots />
        </CustomButton>
      </div>
    );
  };

  const renderStop = () => {
    const unavailableCharger = charger.ports.every(
      (ele) =>
        ele.statusWithoutMaintenance?.toLowerCase() === ConnectorStatus.UNAVAILABLE.toLowerCase() || // statusWithoutMaintenance is optional now
        ele.status.toLowerCase() === ConnectorStatus.UNKNOWN.toLowerCase(),
    );
    return (
      <div className={classes.buttonWrapper}>
        <CustomButton
          variant='contained'
          className={classes.buttonStyles}
          style={{
            backgroundColor: '#F44336',
          }}
          onClick={stopCharge}
          disabled={isChargingBySelf && unavailableCharger}
          data-testid={dataTestIds.chargerPage.stopChargingButton}
        >
          {i18n.t('stop')}
        </CustomButton>
      </div>
    );
  };

  const showStopBtn = () => {
    if (buttonStatus === BUTTON_STATUS.IN_SESSION && activeSession.stopInitiated) {
      return null;
    } else if (loading) {
      return renderLoading();
    } else if (
      buttonStatus === BUTTON_STATUS.IN_SESSION &&
      activeSession.idleFeeStatus !== IdleFeesStatus.IMPOSED
    ) {
      return renderStop();
    }
  };

  if (!sessionDetails) {
    return renderLoading();
  }

  return (
    <Grid container>
      <Grid className={classes.head}>
        {charger?.maintenanceFlag && !showTechView && activeSession && (
          <ChargerSessionWarningComponent />
        )}
      </Grid>
      {!transaction?.stopTime &&
        (activeSession.idleFeeStatus === IdleFeesStatus.GRACE_PERIOD ||
          activeSession.idleFeeStatus === IdleFeesStatus.IMPOSED ||
          activeSession.stopInitiated) && (
          <Grid className={classes.head}>
            {lastCostItem && (
              <ChargerIdlingComponent
                session={activeSession}
                charger={charger}
                lastCostItem={lastCostItem}
              />
            )}
          </Grid>
        )}
      {!transaction?.stopTime &&
        costItemsLength > 1 &&
        lastCostItem &&
        activeSession.idleFeeStatus === IdleFeesStatus.NOT_IMPOSED &&
        lastCostItem.pricingType !== PricingType.IDLE &&
        lastCostItem.periodEnergyKilowattHours > 0 && (
          <Grid className={classes.head} style={{ marginTop: '24px' }}>
            <ChargerRestartedComponent />
          </Grid>
        )}
      {sessionDetails && <ChargerSessionDetails session={sessionDetails} />}
      {!(transactionId && transaction) && isChargingBySelf && showStopBtn()}
      {!transaction?.receiptId && (
        <ChargerCostDetails
          session={activeSession}
          isChargingBySelf={isChargingBySelf}
          charger={charger}
          receipt={receipt}
        />
      )}
      {transaction?.receiptId && isLoggedIn && (
        <Grid item xs={12} sx={{ my: 2 }}>
          <FeedbackWithOptions transaction={transaction} />
        </Grid>
      )}
      {isChargingBySelf && !transaction?.receiptId && theme.show_promo_codes !== false && (
        <PromoCode
          sessionId={activeSession.sessionId}
          appliedPromoCode={getAppliedPromoFromSession(activeSession)}
        />
      )}
      {transaction?.receiptId && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            minWidth: '200px',
            padding: '0',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <SessionOutlineButton
            onClick={() => {
              window.location.href = `/${charger.name}`;
            }}
            label={i18n.t('back_to_the_charger')}
          />
          <SessionButton
            label={i18n.t('view_full_script')}
            handleReceiptClick={handleReceiptClick}
            dataTestId={dataTestIds.chargerPage.receiptButton}
          />
        </div>
      )}
    </Grid>
  );
};

export default withStyles(SessionStyle)(ChargerSession);
